import { logoDarkInvertClass } from "@/lib/logoDarkMode";
import { cn } from "@/lib/utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/ui/dropdownMenu";
import { Bot, LoaderCircle, Sparkles, Workflow } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";

import { type ColumnAutomationActivity, type ColumnAutomationLastRunStatus } from "../lib/columnAutomationActivity";
import { COLUMN_AUTOMATIONS_COPY, type ColumnAutomation, type ColumnAutomationKind } from "../lib/columnAutomations";

export type { ColumnAutomationActivity, ColumnAutomationLastRunStatus };

export type ColumnAutomationRowAction = "settings" | "disable" | "enable" | "remove";

const KIND_FALLBACK_ICON: Partial<Record<ColumnAutomationKind, LucideIcon>> = {
  analysis: Sparkles,
  "risk-score": Sparkles,
  "agent-step": Bot,
  custom: Workflow,
};

interface ColumnAutomationsPopupProps {
  automation: ColumnAutomation;
  onAction: (action: ColumnAutomationRowAction) => void;
  activity?: ColumnAutomationActivity;
  /** Open the menu on first render. Used by stories. */
  defaultOpen?: boolean;
  /** Replaces the default icon button. Used by the header rows. */
  trigger?: ReactNode;
}

/** Header icon. Click opens a menu with the automation summary. */
export function ColumnAutomationsPopup({
  automation,
  onAction,
  activity,
  defaultOpen = false,
  trigger,
}: ColumnAutomationsPopupProps) {
  return (
    <DropdownMenu defaultOpen={defaultOpen}>
      <DropdownMenuTrigger asChild>
        {trigger ?? <ColumnAutomationIconButton automation={automation} />}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56 p-0" data-testid="column-automations-popup">
        <div className="p-1">
          <ColumnAutomationInfo
            automation={automation}
            activity={activity}
            onOpenSettings={() => onAction("settings")}
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Header icon. Used as a direct settings button or as a menu trigger. */
export const ColumnAutomationIconButton = forwardRef<
  HTMLButtonElement,
  { automation: ColumnAutomation } & ButtonHTMLAttributes<HTMLButtonElement>
>(function ColumnAutomationIconButton({ automation, className, ...props }, ref) {
  const needsRepair = automation.health === "needs-repair";
  const disabled = automation.health === "disabled";

  return (
    <button
      type="button"
      aria-label={automation.name}
      title={automation.name}
      data-testid={`column-automation-icon-${automation.id}`}
      className={cn(
        "relative flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
        disabled && "opacity-70",
        className,
      )}
      {...props}
      ref={ref}
    >
      <ColumnAutomationGlyph automation={automation} className="size-3.5" />
      {needsRepair ? (
        <span
          className="absolute -right-0.5 -top-0.5 size-1.5 rounded-full bg-amber-500"
          data-testid={`column-automation-icon-${automation.id}-needs-repair`}
          aria-hidden
        />
      ) : null}
    </button>
  );
});

export function ColumnAutomationGlyph({
  automation,
  className,
}: {
  automation: Pick<ColumnAutomation, "iconSrc" | "iconAlt" | "kind">;
  className?: string;
}) {
  if (automation.iconSrc) {
    return (
      <img
        src={automation.iconSrc}
        alt=""
        className={cn(
          "shrink-0 object-contain",
          className,
          automation.iconAlt === "GitHub" && "dark:brightness-0 dark:invert",
          logoDarkInvertClass(automation.iconSrc),
        )}
      />
    );
  }
  const Icon = KIND_FALLBACK_ICON[automation.kind] ?? Workflow;
  return <Icon className={cn("shrink-0 text-muted-foreground", className)} aria-hidden />;
}

function ColumnAutomationInfo({
  automation,
  activity,
  onOpenSettings,
}: {
  automation: ColumnAutomation;
  activity?: ColumnAutomationActivity;
  onOpenSettings: () => void;
}) {
  const disabled = automation.health === "disabled";
  const needsRepair = automation.health === "needs-repair";

  return (
    <DropdownMenuItem
      data-testid={`column-automation-row-${automation.id}`}
      onSelect={onOpenSettings}
      className={cn("items-start gap-3 py-2.5", disabled && "opacity-70")}
    >
      <ColumnAutomationGlyph automation={automation} className="mt-0.5 size-4" />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{automation.name}</span>
        <span className="mt-0.5 block text-[13px] text-muted-foreground">
          {automation.trigger} → {automation.action}
        </span>
        {activity ? <ColumnAutomationActivitySummary activity={activity} /> : null}
        {needsRepair || disabled ? (
          <span className="mt-1 flex flex-wrap items-center gap-1.5">
            {needsRepair ? (
              <span className="text-[11px] font-medium text-amber-700 dark:text-amber-400">
                {COLUMN_AUTOMATIONS_COPY.needsRepairLabel}
              </span>
            ) : null}
            {disabled ? (
              <span className="text-[11px] font-medium text-muted-foreground">
                {COLUMN_AUTOMATIONS_COPY.disabledLabel}
              </span>
            ) : null}
          </span>
        ) : null}
      </span>
    </DropdownMenuItem>
  );
}

function ColumnAutomationActivitySummary({ activity }: { activity: ColumnAutomationActivity }) {
  const running = activity.runningCount ?? 0;
  const lastRunLabel =
    activity.lastRunStatus === "passed"
      ? COLUMN_AUTOMATIONS_COPY.lastRunPassed
      : activity.lastRunStatus === "failed"
        ? COLUMN_AUTOMATIONS_COPY.lastRunFailed
        : undefined;

  return (
    <span className="mt-1.5 flex flex-col gap-0.5" data-testid="column-automation-activity">
      {lastRunLabel && activity.lastRunWhen ? (
        <span
          className={cn(
            "text-[11px] font-medium",
            activity.lastRunStatus === "passed"
              ? "text-emerald-700 dark:text-emerald-400"
              : "text-red-700 dark:text-red-400",
          )}
        >
          {lastRunLabel}
          <span className="font-normal text-muted-foreground"> · {activity.lastRunWhen}</span>
        </span>
      ) : null}
      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
        {running > 0 ? (
          <LoaderCircle className="size-3 shrink-0 animate-spin text-[color:var(--status-running-dot)]" aria-hidden />
        ) : null}
        {running} {COLUMN_AUTOMATIONS_COPY.activityRunning}
      </span>
    </span>
  );
}
